/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Andrey Dubov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */
import produce from 'immer';
import { Reducer } from 'redux';

import {
    AUTH_SET_IS_FORBIDDEN,
    AUTH_SET_LOGGED_IN,
    initialAuthState,
    LOAD_AUTH_DATA,
    LOAD_AUTH_DATA_FAILURE,
    LOAD_AUTH_DATA_SUCCESSFUL,
    LOGIN_PROCESS,
    LOGIN_PROCESS_ERROR,
    LOGIN_PROCESS_FAILED,
    LOGIN_PROCESS_SUCCESSFUL,
    LOGOUT_START,
    LOGOUT_START_FAILURE,
    LOGOUT_START_SUCCESSFUL,
    REDIRECT_TO_SOHO,
    REDIRECT_TO_SOHO_FAILURE,
} from '../constants/authConstants';
import {
    LOGIN_PROCESS_NEWS_SERVICE_ACCESS_ERROR,
    LOGIN_PROCESS_NEWS_SERVICE_ACCESS_SUCCESSFUL,
    LOGIN_PROCESS_SERVICE_ACCESS,
    LOGIN_PROCESS_SERVICE_ACCESS_ERROR,
    LOGIN_PROCESS_SERVICE_ACCESS_SUCCESSFUL,
} from '../constants/authServiceAccessConstants';
import { AuthTypes, IAuthState } from '../types/authTypes';

/**
 * AuthReducer required to work with login page
 * @param {IAuthState} state
 * @param {AuthTypes} action
 * @returns {Produced<IAuthState, void>}
 */
const authReducer: Reducer<IAuthState> = (state: IAuthState = initialAuthState, action: AuthTypes) =>
    produce(state, (draft) => {
        switch (action.type) {
            case AUTH_SET_IS_FORBIDDEN:
                draft.isForbidden = action.payload.isForbidden;
                break;
            case AUTH_SET_LOGGED_IN:
                draft.loggedIn = action.payload.loggedIn;
                break;
            case LOGIN_PROCESS:
            case LOGIN_PROCESS_SERVICE_ACCESS:
                break;
            case LOGIN_PROCESS_SUCCESSFUL:
                draft.data = action.payload;
                draft.loggedIn = true;
                break;
            case LOGIN_PROCESS_ERROR:
                draft.errorMessage = action.payload;
                draft.loggedIn = false;
                break;
            case LOGIN_PROCESS_FAILED:
                draft.errorMessage = action.payload;
                break;
            case LOGIN_PROCESS_SERVICE_ACCESS_ERROR:
                draft.isServiceAccessLoggedIn = false;
                break;
            case LOGIN_PROCESS_SERVICE_ACCESS_SUCCESSFUL:
                draft.isServiceAccessLoggedIn = true;
                break;
            case LOGIN_PROCESS_NEWS_SERVICE_ACCESS_ERROR:
                draft.isNewsServiceAccessLoggedIn = false;
                break;
            case LOGIN_PROCESS_NEWS_SERVICE_ACCESS_SUCCESSFUL:
                draft.isNewsServiceAccessLoggedIn = true;
                break;

            case LOAD_AUTH_DATA:
                draft.isLoading = true;
                break;
            case LOAD_AUTH_DATA_SUCCESSFUL:
                draft.data = action.payload;
                draft.loggedIn = true;
                draft.isLoading = false;
                break;
            case LOAD_AUTH_DATA_FAILURE:
                draft.errorMessage = action.payload;
                draft.loggedIn = false;
                draft.isLoading = false;
                break;
            case LOGOUT_START:
                break;
            case LOGOUT_START_SUCCESSFUL:
                return initialAuthState;
            case LOGOUT_START_FAILURE:
                draft.loggedIn = false;
                draft.errorMessage = action.payload;
                break;
            case REDIRECT_TO_SOHO:
                break;
            case REDIRECT_TO_SOHO_FAILURE:
                draft.loggedIn = false;
                draft.errorMessage = action.payload;
                break;
            default:
                break;
        }
    });

export default authReducer;
